"use strict";
/**
 * Void a previously synced Handshake/Nucleus project invoice ACCREC in Xero.
 *
 * ## Trigger
 * When a Nucleus invoice that was pushed to Xero is no longer collectible locally:
 * - PENDING invoices hard-deleted during payment-plan recreate
 * - Invoice cancelled or closed
 *
 * Only voids **AUTHORISED unpaid** ACCRECs. Invoices with payments applied cannot be voided via Xero
 * (manual cleanup required). Already VOIDED/DELETED is treated as success (idempotent).
 *
 * ## Operation
 * {@link ACCOUNTING_SYNC_OPERATION_VOID_XERO_INVOICE}
 *
 * ## Idempotency key
 * `${ACCOUNTING_INVOICE_VOID_IDEMPOTENCY_PREFIX}${projectInvoiceId}:${xeroInvoiceId}`
 *
 * Scoped by Xero invoice id so payment-plan recreate can reuse the same local `projectInvoiceId`
 * (void old ACCREC → clear create idempotency → sync a replacement ACCREC → void that later).
 * Legacy keys `${PREFIX}${projectInvoiceId}` (no Xero id) may still exist from earlier deploys.
 *
 * After a successful void, the matching **create** idempotency row (`project_invoice:{id}`) must be
 * removed so a replacement invoice with the same local id can auto-sync instead of replaying the
 * voided ACCREC reference.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ACCOUNTING_INVOICE_VOID_IDEMPOTENCY_PREFIX = exports.ACCOUNTING_SYNC_OPERATION_VOID_XERO_INVOICE = void 0;
exports.buildAccountingInvoiceVoidIdempotencyKey = buildAccountingInvoiceVoidIdempotencyKey;
exports.parseAccountingInvoiceVoidIdempotencyKey = parseAccountingInvoiceVoidIdempotencyKey;
exports.ACCOUNTING_SYNC_OPERATION_VOID_XERO_INVOICE = "VOID_XERO_INVOICE";
exports.ACCOUNTING_INVOICE_VOID_IDEMPOTENCY_PREFIX = "project_invoice_void:";
/** Builds `project_invoice_void:{projectInvoiceId}:{xeroInvoiceId}`. */
function buildAccountingInvoiceVoidIdempotencyKey(projectInvoiceId, xeroInvoiceId) {
    return `${exports.ACCOUNTING_INVOICE_VOID_IDEMPOTENCY_PREFIX}${projectInvoiceId}:${xeroInvoiceId}`;
}
/**
 * Parses void idempotency keys.
 * Supports current `…:{invoiceId}:{xeroInvoiceId}` and legacy `…:{invoiceId}`.
 */
function parseAccountingInvoiceVoidIdempotencyKey(idempotencyKey) {
    if (!idempotencyKey.startsWith(exports.ACCOUNTING_INVOICE_VOID_IDEMPOTENCY_PREFIX)) {
        return null;
    }
    const rest = idempotencyKey.slice(exports.ACCOUNTING_INVOICE_VOID_IDEMPOTENCY_PREFIX.length);
    if (!rest) {
        return null;
    }
    const colon = rest.indexOf(":");
    if (colon <= 0) {
        return { projectInvoiceId: rest, xeroInvoiceId: null };
    }
    const projectInvoiceId = rest.slice(0, colon);
    const xeroInvoiceId = rest.slice(colon + 1).trim();
    if (!projectInvoiceId) {
        return null;
    }
    return { projectInvoiceId, xeroInvoiceId: xeroInvoiceId || null };
}
